import { useCallback, useEffect, useState } from 'react';

// TheSportsDB free tier: no sign-up, CORS-enabled, one next and one last event.
const API = 'https://www.thesportsdb.com/api/v1/json/123';
export const TEAM_ID = '133612';
const LIVE_REFRESH_MS = 2 * 60 * 1000;

export interface SportsEvent {
  idEvent: string;
  strHomeTeam: string;
  strAwayTeam: string;
  idHomeTeam: string;
  strLeague: string;
  intRound: string | null;
  strTimestamp: string;
  strVenue: string | null;
  strStatus: string | null;
  intHomeScore: string | null;
  intAwayScore: string | null;
  strHomeTeamBadge: string | null;
  strAwayTeamBadge: string | null;
}

export const LIVE_STATUS: Record<string, string> = {
  '1H': '1st half',
  HT: 'Half time',
  '2H': '2nd half',
  ET: 'Extra time',
  BT: 'Break',
  P: 'Penalties',
  LIVE: 'In play',
};

export const FINISHED = ['FT', 'AET', 'PEN', 'Match Finished'];

export const kickOff = (event: SportsEvent) => new Date(`${event.strTimestamp}Z`);

export const resultFor = (event: SportsEvent) => {
  const home = Number(event.intHomeScore);
  const away = Number(event.intAwayScore);
  const atHome = event.idHomeTeam === TEAM_ID;
  const ours = atHome ? home : away;
  const theirs = atHome ? away : home;

  return {
    ours,
    theirs,
    opponent: atHome ? event.strAwayTeam : event.strHomeTeam,
    outcome: ours > theirs ? 'W' : ours < theirs ? 'L' : 'D',
  };
};

const isToday = (event: SportsEvent | null) =>
  !!event && kickOff(event).toDateString() === new Date().toDateString();

export interface UnitedFixtures {
  next: SportsEvent | null;
  last: SportsEvent | null;
  loading: boolean;
  liveLabel?: string;
  // Today's match, whether still to come, in play or finished.
  today: SportsEvent | null;
}

const useUnitedFixtures = (): UnitedFixtures => {
  const [next, setNext] = useState<SportsEvent | null>(null);
  const [last, setLast] = useState<SportsEvent | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [nextRes, lastRes] = await Promise.allSettled([
      fetch(`${API}/eventsnext.php?id=${TEAM_ID}`).then((r) => r.json()),
      fetch(`${API}/eventslast.php?id=${TEAM_ID}`).then((r) => r.json()),
    ]);

    if (nextRes.status === 'fulfilled') setNext(nextRes.value.events?.[0] ?? null);
    if (lastRes.status === 'fulfilled') setLast(lastRes.value.results?.[0] ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const liveLabel = next?.strStatus ? LIVE_STATUS[next.strStatus] : undefined;

  useEffect(() => {
    if (!liveLabel) return;
    const timer = window.setInterval(load, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [liveLabel, load]);

  const todayEvent = isToday(next) ? next : isToday(last) ? last : null;
  const today = todayEvent?.strStatus === 'PST' ? null : todayEvent;

  return { next, last, loading, liveLabel, today };
};

export default useUnitedFixtures;
